import {
  Autocomplete,
  AutocompleteItem,
  Checkbox,
  CheckboxGroup,
  DatePicker,
  Input,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  Switch,
  Textarea,
  TimeInput,
  type TimeInputValue as TimeValue,
} from '@heroui/react';
import { parseDate, parseTime, type CalendarDate } from '@internationalized/date';
import { instantToZonedWallClock, zonedWallClockToInstant } from '@lims/shared-types';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { POPOVER_MOTION_CLASS } from './popover-motion.js';

/**
 * Form fields.
 *
 * Every field in the product is one of these. The point is not to wrap HeroUI
 * for its own sake: it is that validation presentation, required marking,
 * description placement and error wiring are decided ONCE, so no page invents
 * its own way of showing that a value is wrong.
 *
 * Shared rules:
 *  - `label` and `description` arrive already translated. A literal here is a
 *    lint error (lims/no-hardcoded-text).
 *  - `error` is a translated message resolved from the shared Zod schema, so
 *    the frontend and backend cannot disagree about what is valid — see
 *    useZodForm.
 *  - An EMPTY FIELD IS NEVER ZERO (CLAUDE.md section 8). Numeric fields keep
 *    the empty string as empty and hand back `null`, never `0`.
 *  - Nothing sets `dir` per field. Direction comes from the document root, so
 *    a field mirrors with the page rather than against it.
 */

interface CommonFieldProps {
  readonly label: string;
  readonly description?: string;
  readonly error?: string;
  /**
   * Marked invalid without a message of its own — one half of a bilingual
   * pair whose problem is the pair as a whole, said once beneath it.
   */
  readonly isInvalid?: boolean;
  readonly isRequired?: boolean;
  readonly isDisabled?: boolean;
  readonly isReadOnly?: boolean;
  readonly name?: string;
  readonly className?: string;
}

/**
 * Props every HeroUI input takes the same way.
 *
 * THE LABEL ALWAYS SITS ABOVE THE BOX (`outside-top`). With plain `outside`,
 * HeroUI draws the label INSIDE an empty box that has no placeholder and lifts
 * it above once a value is typed — so on one form "Quantity" (filled) sat above
 * its box while "Unit price" (empty) sat inside its own, and the same field
 * changed shape as it was filled (found in the UX pass, quotation form). A
 * label in one fixed place is what makes a form read as a grid. Date pickers
 * keep `outside`: they always show their segments, so their label never drops
 * inside, and they do not take `outside-top`.
 */
function sharedProps(
  props: CommonFieldProps,
  labelPlacement: 'outside' | 'outside-top' = 'outside-top',
) {
  return {
    label: props.label,
    ...(props.description ? { description: props.description } : {}),
    ...(props.error ? { errorMessage: props.error } : {}),
    isInvalid: Boolean(props.error) || props.isInvalid === true,
    isRequired: props.isRequired ?? false,
    isDisabled: props.isDisabled ?? false,
    ...(props.name ? { name: props.name } : {}),
    ...(props.className ? { className: props.className } : {}),
    variant: 'bordered' as const,
    labelPlacement,
  };
}

/**
 * The resting look of every bordered field, in one place.
 *
 *  - A WHITE fill, so a field reads as a field on the grey page ground as well
 *    as on a white card (it was transparent, and on the page it was grey).
 *  - A border in `FIELD_BORDER_CLASS` — see its note for the measured ratio.
 *  - A placeholder in `default-600`, the lightest readable ink, which keeps
 *    it clearly lighter than a typed value (the ink, `foreground`) without
 *    vanishing. HeroUI's own placeholder step measured 1.75:1.
 *  - A read-only field is visibly different: a sunken fill and no hover, so a
 *    value that cannot be changed does not look like one that can.
 */
export const FIELD_BORDER_CLASS = 'border-default-500';

const FIELD_REST = `bg-content1 ${FIELD_BORDER_CLASS} data-[hover=true]:border-default-600`;
const FIELD_READ_ONLY =
  'group-data-[readonly=true]:bg-default-100 group-data-[readonly=true]:border-default-300';

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

/**
 * A focused field keeps a visible, ink-coloured border.
 *
 * HeroUI draws the border of a focused bordered field in `default-foreground`,
 * and in this theme `default.foreground` is WHITE — the text on a solid neutral
 * fill, where it is right (`hero.ts`). On a white card a field clicked into
 * therefore LOST its border (found in the Phase 10 browser check: a text area
 * in a white dialog measured a white border once focused; the blue focus ring
 * covers only keyboard focus). Each class below names the same modifiers as
 * the theme's own, so the class merge replaces the white rather than competing
 * with it. The same inversion is why the select, the date segments and the
 * tertiary action carry their own ink (`SELECT_READABILITY`, `DATE_READABILITY`,
 * `ActionButton`).
 */
export const FIELD_FOCUS_BORDER = {
  inputWrapper: `${FIELD_REST} ${FIELD_READ_ONLY} group-data-[focus=true]:border-foreground group-data-[focus=true]:data-[hover=true]:border-foreground`,
  input: 'placeholder:text-default-600',
} as const;

export interface TextFieldProps extends CommonFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'time';
  readonly autoComplete?: string;
  readonly endContent?: ReactNode;
  /**
   * The direction of the TEXT inside the box, when it is not the page's — the
   * Arabic half of a bilingual field on an English page, say. Only the typed
   * text turns; the label, the required mark and the description keep the
   * page's direction.
   */
  readonly textDirection?: 'ltr' | 'rtl' | 'auto';
}

export function TextField({
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete,
  endContent,
  isReadOnly,
  textDirection,
  ...common
}: TextFieldProps) {
  return (
    <Input
      {...sharedProps(common)}
      classNames={FIELD_FOCUS_BORDER}
      type={type}
      value={value}
      onValueChange={onChange}
      isReadOnly={isReadOnly ?? false}
      {...(placeholder ? { placeholder } : {})}
      {...(autoComplete ? { autoComplete } : {})}
      {...(endContent ? { endContent } : {})}
      {...(textDirection ? { dir: textDirection } : {})}
    />
  );
}

export interface TextAreaFieldProps extends CommonFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly rows?: number;
  /**
   * The direction of the TEXT inside the box, when it is not the page's: a
   * CSV file is left-to-right data even on an Arabic page (Phase 12 browser
   * check). The label and description keep the page's direction.
   */
  readonly textDirection?: 'ltr' | 'rtl' | 'auto';
}

export function TextAreaField({
  value,
  onChange,
  placeholder,
  rows = 4,
  isReadOnly,
  textDirection,
  ...common
}: TextAreaFieldProps) {
  return (
    <Textarea
      {...sharedProps(common)}
      classNames={FIELD_FOCUS_BORDER}
      value={value}
      onValueChange={onChange}
      // Passed on exactly as `TextField` does. `sharedProps` does not carry it,
      // and a text area that silently dropped it offered a reader without the
      // right to edit a box that accepted typing.
      isReadOnly={isReadOnly ?? false}
      minRows={rows}
      {...(placeholder ? { placeholder } : {})}
      {...(textDirection ? { dir: textDirection } : {})}
    />
  );
}

/**
 * A number field that distinguishes empty from zero.
 *
 * This is a functional requirement, not a nicety: a blank moisture reading and
 * a reading of 0 mean entirely different things, and conflating them would
 * fabricate data.
 */
export interface NumberFieldProps extends CommonFieldProps {
  readonly value: number | null;
  readonly onChange: (value: number | null) => void;
  readonly placeholder?: string;
  readonly step?: string;
  readonly unit?: string;
}

export function NumberField({
  value,
  onChange,
  placeholder,
  step,
  unit,
  isReadOnly,
  ...common
}: NumberFieldProps) {
  return (
    <Input
      {...sharedProps(common)}
      classNames={FIELD_FOCUS_BORDER}
      type="number"
      inputMode="decimal"
      // Passed on explicitly, as `TextField` and `TextAreaField` do:
      // `sharedProps` does not carry it, and a number field that dropped it
      // accepted typing on a record the page had declared read-only — a
      // locked quotation's quantities, for one (UX pass).
      isReadOnly={isReadOnly ?? false}
      value={value === null ? '' : String(value)}
      onValueChange={(next) => {
        const trimmed = next.trim();
        // Empty stays empty. It is NOT coerced to 0.
        if (trimmed === '') return onChange(null);
        const parsed = Number(trimmed);
        onChange(Number.isNaN(parsed) ? null : parsed);
      }}
      {...(placeholder ? { placeholder } : {})}
      {...(step ? { step } : {})}
      {...(unit ? { endContent: <FieldUnit>{unit}</FieldUnit> } : {})}
    />
  );
}

/** A unit suffix. Marked `aria-hidden` because the label already names it. */
function FieldUnit({ children }: { children: ReactNode }) {
  return (
    <span aria-hidden="true" className="text-caption text-default-600">
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Choice
// ---------------------------------------------------------------------------

export interface SelectOption {
  readonly value: string;
  /** Already translated. */
  readonly label: string;
  readonly description?: string;
  readonly isDisabled?: boolean;
}

export interface SelectFieldProps extends CommonFieldProps {
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly options: readonly SelectOption[];
  readonly placeholder?: string;
  /**
   * The options are still arriving for the FIRST time. The field stays
   * mounted and shows a spinner; a background refetch is not a load (the
   * query keeps its data), so pass the query's `isLoading` (its first fetch
   * in flight), never `isFetching`.
   */
  readonly isLoading?: boolean;
  /**
   * Whether an optional select lists its placeholder as a row that takes the
   * choice back. On by default when there is a placeholder; off where the
   * placeholder is an instruction ("Choose a service") rather than an answer.
   */
  readonly offerNoChoice?: boolean;
}

/**
 * The key of the "no choice" row an optional select offers. Chosen so it can
 * never collide with a record id or a catalogue code.
 */
const NO_CHOICE_KEY = '__no_choice__';

/**
 * The props that keep a select readable and reachable. Found in a real browser
 * during the Phase 10 check, not assumed:
 *
 *  - THE CHOSEN VALUE WAS WHITE. The bordered variant colours a chosen value
 *    `text-default-foreground`, and in this theme `default.foreground` is white
 *    — it is the text colour ON a solid neutral button. On the white trigger the
 *    choice vanished, so a page with a preselected template looked as if
 *    nothing was chosen. The value takes the ink colour instead.
 *  - THE LABEL SAT ON THE TRIGGER. With `outside` placement, a placeholder and a
 *    description together, HeroUI positions the label at half the height of the
 *    whole field (description included), which drew it over the trigger's lower
 *    edge. `outside-top` always sets the label in the flow above the trigger,
 *    which is where every other field in the product puts it.
 *  - THE OPTIONS WERE HIDDEN FROM ASSISTIVE TECHNOLOGY INSIDE A DIALOG. The list
 *    opens in a layer appended to the document, and the open dialog marks every
 *    node appended outside it `aria-hidden`. The dialog and the list use two
 *    different copies of that hiding logic, so the list never told the dialog it
 *    was on top. `data-react-aria-top-layer` is react-aria's own marker for a
 *    layer that must stay exposed (it is how toasts survive a modal).
 */
const SELECT_READABILITY = {
  labelPlacement: 'outside-top',
  classNames: {
    // A PLACEHOLDER ("Choose a client") and a CHOSEN value must not look the
    // same. Both measured as the full ink in the UX pass, so an empty required
    // select read as filled. The placeholder takes `default-600`, the chosen
    // value the ink.
    value: 'text-default-600 group-data-[has-value=true]:text-foreground',
    // The open or focused box keeps an ink border, for FIELD_FOCUS_BORDER's reason.
    trigger: `${FIELD_REST} data-[open=true]:border-foreground data-[focus=true]:border-foreground data-[focus=true]:data-[hover=true]:border-foreground`,
    // The list's own open and close motion (`popover-motion.ts`, D196).
    popoverContent: POPOVER_MOTION_CLASS,
  },
  popoverProps: { 'data-react-aria-top-layer': 'true' } as Record<string, string>,
} as const;

/**
 * A single choice from a list.
 *
 * WHY EVERY DROPDOWN FLASHED (UX follow-up, measured frame by frame in a real
 * browser — `PROJECT_DOCUMENTATION.md` section 15.26). Nothing remounted and no
 * refetch blanked a value. Three shared causes did:
 *
 *  - THE LIST'S OWN ANIMATION. HeroUI animates the popover with framer-motion,
 *    which hands opacity and scale to the browser's animation engine. When that
 *    animation ends there is one frame in which it has been removed but its
 *    final value not yet written: after opening, the list turned INVISIBLE for
 *    a frame; after closing — which is also what choosing does — the fully
 *    drawn list came BACK for a frame before it went. The list sits on the
 *    field, so the field appeared to flash on open, select, close and change.
 *    `disableAnimation` removes the animation, and with it the gap. The motion
 *    a list has now is a CSS keyframe and a fading inert copy on close, which
 *    have no such frame (`popover-motion.ts`, decision D196).
 *  - ESCAPE AND A SECOND CLICK ERASED THE VALUE. react-aria clears a
 *    selection when Escape is pressed in an open list, and a single-choice
 *    list deselects an option clicked again. Both reached `onChange(null)`, so
 *    a person closing the list or confirming the same choice lost it.
 *    `disallowEmptySelection` stops both; clearing an OPTIONAL choice is an
 *    explicit row instead (below), never an accident.
 *  - A FIRST LOAD LOOKED LIKE "NOTHING CHOSEN". While the options were still
 *    arriving, a set value had no row to show, so the field displayed its
 *    placeholder ("Not linked to a job") and then the value appeared. With
 *    `isLoading` the field shows a spinner instead and stays mounted.
 *
 * The chosen key is memoised: rebuilt on every render, it gave react-aria a
 * new selection object each time the parent rendered.
 */
export function SelectField({
  value,
  onChange,
  options,
  placeholder,
  isLoading = false,
  offerNoChoice = true,
  ...common
}: SelectFieldProps) {
  const selectedKeys = useMemo(
    () => (value === null ? new Set<string>() : new Set([value])),
    [value],
  );
  // An optional choice can be taken back. The row carries the page's own
  // wording for "nothing chosen" — "None", "All", "Not linked to a job" — so
  // the reader sees the same words in the list as on the empty field.
  const noChoiceLabel =
    offerNoChoice && placeholder && !common.isRequired && !common.isReadOnly
      ? placeholder
      : null;
  const items: readonly SelectOption[] = noChoiceLabel
    ? [{ value: NO_CHOICE_KEY, label: noChoiceLabel }, ...options]
    : options;

  return (
    <Select
      {...sharedProps(common)}
      {...SELECT_READABILITY}
      selectedKeys={selectedKeys}
      disallowEmptySelection
      disableAnimation
      isLoading={isLoading}
      onSelectionChange={(keys) => {
        const [first] = Array.from(keys as Set<string>);
        if (first === undefined) return;
        const next = first === NO_CHOICE_KEY ? null : String(first);
        if (next !== value) onChange(next);
      }}
      {...(placeholder ? { placeholder } : {})}
    >
      {items.map((option) => (
        <SelectItem
          key={option.value}
          textValue={option.label}
          {...(option.description ? { description: option.description } : {})}
          isDisabled={option.isDisabled ?? false}
        >
          {option.label}
        </SelectItem>
      ))}
    </Select>
  );
}

export interface SearchSelectFieldProps extends CommonFieldProps {
  /** `null` shows an empty box with its placeholder. */
  readonly value: string | null;
  readonly onChange: (value: string) => void;
  /**
   * The rows to offer for what has been typed. The CALLER searches — usually
   * on the server — and this field never filters them again, so a list of any
   * length is narrowed where the data is.
   */
  readonly options: readonly SelectOption[];
  /** What the person typed, or an empty string when the box shows the choice. */
  readonly onSearchChange: (text: string) => void;
  /**
   * The words for the current choice when it is not among `options` — a
   * record chosen from a link, say, before any search has returned it.
   */
  readonly selectedLabel?: string;
  readonly placeholder?: string;
  /** Said in the open list when nothing matches what was typed. */
  readonly emptyContent?: string;
  /** A search is in flight: the list shows a spinner and keeps its rows. */
  readonly isLoading?: boolean;
}

/**
 * A single choice from a list too long to send whole: the person types and
 * the caller answers with the matching rows (D322).
 *
 * THE BOX SHOWS THE CHOICE UNTIL THE PERSON TYPES. Typing narrows the list;
 * leaving the box without choosing puts the current choice's words back, so a
 * half-typed search is never mistaken for a filter that is in force. A choice
 * is never taken back by accident: there is no clear button, and an optional
 * field offers its "nothing chosen" answer as a row, as `SelectField` does.
 *
 * It keeps `SelectField`'s readable trigger, top-layer list and list motion,
 * for the same measured reasons (`SELECT_READABILITY`).
 */
export function SearchSelectField({
  value,
  onChange,
  options,
  onSearchChange,
  selectedLabel,
  placeholder,
  emptyContent,
  isLoading = false,
  ...common
}: SearchSelectFieldProps) {
  const chosenLabel =
    options.find((option) => option.value === value)?.label ?? selectedLabel ?? '';
  const [typed, setTyped] = useState<string | null>(null);

  return (
    <Autocomplete
      {...sharedProps(common)}
      inputValue={typed ?? chosenLabel}
      onInputChange={(text) => {
        // react-aria also writes the chosen row's words here; that is not a search.
        const searching = text !== chosenLabel;
        setTyped(searching ? text : null);
        onSearchChange(searching ? text : '');
      }}
      selectedKey={value}
      onSelectionChange={(key) => {
        setTyped(null);
        onSearchChange('');
        if (key !== null && String(key) !== value) onChange(String(key));
      }}
      onOpenChange={(isOpen) => {
        if (!isOpen && typed !== null) {
          setTyped(null);
          onSearchChange('');
        }
      }}
      items={options}
      // The caller already searched; filtering again would hide its answer.
      defaultFilter={() => true}
      menuTrigger="focus"
      isClearable={false}
      isLoading={isLoading}
      allowsEmptyCollection
      disableAnimation
      {...(placeholder ? { placeholder } : {})}
      inputProps={{ classNames: FIELD_FOCUS_BORDER }}
      listboxProps={emptyContent ? { emptyContent } : {}}
      popoverProps={{
        ...SELECT_READABILITY.popoverProps,
        classNames: { content: POPOVER_MOTION_CLASS },
      }}
    >
      {(option) => (
        <AutocompleteItem
          key={option.value}
          textValue={option.label}
          {...(option.description ? { description: option.description } : {})}
        >
          {option.label}
        </AutocompleteItem>
      )}
    </Autocomplete>
  );
}

export interface CheckboxFieldProps {
  readonly label: string;
  readonly description?: string;
  readonly value: boolean;
  readonly onChange: (value: boolean) => void;
  readonly isDisabled?: boolean;
}

export function CheckboxField({
  label,
  description,
  value,
  onChange,
  isDisabled = false,
}: CheckboxFieldProps) {
  return (
    <Checkbox
      isSelected={value}
      onValueChange={onChange}
      isDisabled={isDisabled}
    >
      <span className="text-body">{label}</span>
      {description ? (
        <span className="block text-caption text-default-600">
          {description}
        </span>
      ) : null}
    </Checkbox>
  );
}

export interface CheckboxGroupFieldProps extends CommonFieldProps {
  readonly value: readonly string[];
  readonly onChange: (value: string[]) => void;
  readonly options: readonly SelectOption[];
}

export function CheckboxGroupField({
  value,
  onChange,
  options,
  ...common
}: CheckboxGroupFieldProps) {
  return (
    <CheckboxGroup
      label={common.label}
      {...(common.description ? { description: common.description } : {})}
      {...(common.error ? { errorMessage: common.error } : {})}
      isInvalid={Boolean(common.error)}
      isDisabled={common.isDisabled ?? false}
      value={[...value]}
      onValueChange={onChange}
    >
      {options.map((option) => (
        <Checkbox
          key={option.value}
          value={option.value}
          isDisabled={option.isDisabled ?? false}
        >
          <span className="text-body">{option.label}</span>
          {/* A second line inside the label, so it is part of the box's name. */}
          {option.description ? (
            <span className="block text-caption text-default-600">{option.description}</span>
          ) : null}
        </Checkbox>
      ))}
    </CheckboxGroup>
  );
}

export interface RadioFieldProps extends CommonFieldProps {
  readonly value: string | null;
  readonly onChange: (value: string) => void;
  readonly options: readonly SelectOption[];
  readonly orientation?: 'vertical' | 'horizontal';
  /**
   * Name the group for assistive technology only. For a group that is the
   * whole of a form section whose legend already says the same words — shown
   * twice, one under the other, it read as a stutter (UX pass, quotation form).
   */
  readonly isLabelHidden?: boolean;
}

export function RadioField({
  value,
  onChange,
  options,
  orientation = 'vertical',
  isLabelHidden = false,
  ...common
}: RadioFieldProps) {
  return (
    <RadioGroup
      {...(isLabelHidden ? { 'aria-label': common.label } : { label: common.label })}
      {...(common.description ? { description: common.description } : {})}
      {...(common.error ? { errorMessage: common.error } : {})}
      isInvalid={Boolean(common.error)}
      isDisabled={common.isDisabled ?? false}
      orientation={orientation}
      value={value ?? ''}
      onValueChange={onChange}
    >
      {options.map((option) => (
        <Radio
          key={option.value}
          value={option.value}
          isDisabled={option.isDisabled ?? false}
          {...(option.description ? { description: option.description } : {})}
        >
          {option.label}
        </Radio>
      ))}
    </RadioGroup>
  );
}

export interface SwitchFieldProps {
  readonly label: string;
  readonly description?: string;
  readonly value: boolean;
  readonly onChange: (value: boolean) => void;
  readonly isDisabled?: boolean;
}

export function SwitchField({
  label,
  description,
  value,
  onChange,
  isDisabled = false,
}: SwitchFieldProps) {
  return (
    <Switch
      isSelected={value}
      onValueChange={onChange}
      isDisabled={isDisabled}
      size="sm"
    >
      <span className="text-body">{label}</span>
      {description ? (
        <span className="block text-caption text-default-600">
          {description}
        </span>
      ) : null}
    </Switch>
  );
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/**
 * The empty segments of a date — "dd/mm/yyyy", or "يوم/شهر/سنة" — and the
 * separators between them, in a readable ink. HeroUI draws both in
 * `foreground-500` (#B4BCC8), which measured 1.75:1 on the page ground in the
 * Phase 10 browser check: the format a person has to type in was all but
 * invisible. `default-600` is the lightest step the palette allows for anything
 * readable (`theme/palette.ts`). The picker hands its `classNames` to the date
 * input, and each class names the SAME modifiers as the theme's own, so the
 * class merge replaces the theme's ink rather than competing with it. A filled
 * segment keeps the theme's full ink.
 *
 * The segment being TYPED IN was worse: HeroUI draws it in `default-foreground`,
 * white in this theme, on a pale focus fill — the digit a person was entering
 * could not be read (Phase 10 browser check, the delivery dialog). It takes the
 * ink too, and a focused date field keeps an ink border (FIELD_FOCUS_BORDER).
 */
const DATE_READABILITY = {
  segment:
    'text-default-600 data-[editable=true]:data-[placeholder=true]:text-default-600 data-[editable=true]:focus:text-foreground',
  inputWrapper: `${FIELD_REST} focus-within:border-foreground focus-within:hover:border-foreground`,
  // THE VALUE READS IN CHRONOLOGICAL ORDER IN BOTH LANGUAGES. Inside an Arabic
  // (RTL) page the segments inherited right-to-left order, so a time showed
  // minute before hour and the period (ص/م) on the wrong side. The segment row
  // alone is isolated left-to-right — hour:minute period, day/month/year — and
  // still sits at the start edge of an RTL field. The page, label and helper
  // text keep the page direction; the stored instant is unchanged.
  input: '[direction:ltr] [unicode-bidi:isolate] rtl:justify-end',
} as const;

/** The date picker's classes: readability, plus its calendar's motion (D196). */
const DATE_PICKER_CLASSES = { ...DATE_READABILITY, popoverContent: POPOVER_MOTION_CLASS } as const;

/**
 * A calendar date, held as an ISO `YYYY-MM-DD` string.
 *
 * Deliberately a DATE, not a timestamp: a sampling date has no time zone, and
 * storing one would move it across a midnight boundary somewhere. Timestamps
 * are a separate concern, stored as UTC and formatted for display.
 *
 * The calendar the picker shows follows the locale HeroUIProvider is given —
 * Gregorian in both languages by default (PLAN Q18). Hijri display, where the
 * laboratory enables it, is a secondary READING of a date, not a second way of
 * entering one, so it belongs in formatting rather than here.
 */
export interface DateFieldProps extends CommonFieldProps {
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
}

export function DateField({
  value,
  onChange,
  ...common
}: DateFieldProps) {
  let parsed: CalendarDate | null = null;
  if (value) {
    try {
      parsed = parseDate(value);
    } catch {
      // A malformed stored value shows as empty rather than throwing the page
      // away. The field still reports the value it holds through `error`.
      parsed = null;
    }
  }

  return (
    <DatePicker
      {...sharedProps(common, 'outside')}
      value={parsed}
      // Typed through `CalendarDate` explicitly: the picker's own value type is
      // a union over every date representation, and the inferred parameter
      // collapses to `never` for the calendar-date-only case this field uses.
      onChange={(next: CalendarDate | null) =>
        onChange(next ? next.toString() : null)
      }
      showMonthAndYearPickers
      // No HeroUI popover animation: its last frame flashed (see SelectField).
      disableAnimation
      classNames={DATE_PICKER_CLASSES}
    />
  );
}

/**
 * A moment: a date AND a time, on the LABORATORY's clock.
 *
 * Separate from `DateField` on purpose. Some facts this system records are
 * whole days — a promised date, a contract start — and some are instants: when
 * a sample arrived at the counter, when it was handed to a technician. Offering
 * only a date picker for an instant would silently record midnight as the
 * arrival time, which is a value nobody stated (CLAUDE.md section 8).
 *
 * WHY THIS IS TWO CONTROLS (UX follow-up, `PROJECT_DOCUMENTATION.md` 15.26).
 * It was one HeroUI date picker with minute granularity. That picker holds a
 * day chosen in its calendar in a HIDDEN draft until a time is complete, and
 * shows nothing of it in the field; the time box inside the calendar looked
 * empty and disabled; and closing the calendar with only a day chosen
 * committed it at a placeholder time — midnight. On the owner's screen the day
 * was highlighted, the field still read "dd / mm / yyyy, --:--", and nothing
 * stuck. Now the date and the time are separate, visible controls: each keeps
 * what was entered the moment it is entered, closing and reopening the
 * calendar changes nothing, and the time is typed straight into its box.
 * Nothing is ever defaulted — until BOTH halves are present the field hands
 * back `null`, and says which half is missing.
 *
 * THE CLOCK IS THE LABORATORY'S, NOT THE BROWSER'S. The value in and out is an
 * INSTANT (ISO 8601). What is shown and typed is the wall clock in `timeZone`
 * — the laboratory's IANA zone from its settings (`useLaboratoryTimeZone`) —
 * and the instant handed back carries that zone's offset
 * (`2026-09-16T14:30:00+03:00`). The conversion happens here, once, so no
 * caller can read the time on the wrong clock. The previous callers used the
 * browser's zone, and one sent a bare wall-clock string the API refuses.
 */
export interface DateTimeFieldProps extends CommonFieldProps {
  /** An instant (ISO 8601), or `null`. */
  readonly value: string | null;
  /** An instant with the laboratory's offset, or `null` while incomplete. */
  readonly onChange: (value: string | null) => void;
  /** The laboratory's IANA timezone; `null` while the session is loading. */
  readonly timeZone: string | null;
}

interface MomentDraft {
  readonly date: string | null;
  readonly time: string | null;
}

const EMPTY_MOMENT: MomentDraft = { date: null, time: null };

function momentDraft(value: string | null, timeZone: string | null): MomentDraft {
  if (!value || !timeZone) return EMPTY_MOMENT;
  const wallClock = instantToZonedWallClock(value, timeZone);
  return wallClock
    ? { date: wallClock.slice(0, 10), time: wallClock.slice(11, 16) }
    : EMPTY_MOMENT;
}

function momentInstant(draft: MomentDraft, timeZone: string | null): string | null {
  if (!draft.date || !draft.time || !timeZone) return null;
  return zonedWallClockToInstant(`${draft.date}T${draft.time}`, timeZone);
}

function sameInstant(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  return Date.parse(a) === Date.parse(b);
}

function parseOrNull<T>(raw: string | null, parse: (raw: string) => T): T | null {
  if (!raw) return null;
  try {
    return parse(raw);
  } catch {
    return null;
  }
}

const twoDigits = (value: number) => String(value).padStart(2, '0');

export function DateTimeField({
  value,
  onChange,
  timeZone,
  ...common
}: DateTimeFieldProps) {
  const { t } = useTranslation();
  const labelId = useId();
  const helperId = useId();
  const [draft, setDraft] = useState<MomentDraft>(() => momentDraft(value, timeZone));
  const [source, setSource] = useState({ value, timeZone });

  // A value set from OUTSIDE (a record loaded, a form reset) replaces the
  // draft; the value this field itself just handed back does not.
  if (source.value !== value || source.timeZone !== timeZone) {
    setSource({ value, timeZone });
    if (source.timeZone !== timeZone || !sameInstant(momentInstant(draft, timeZone), value)) {
      setDraft(momentDraft(value, timeZone));
    }
  }

  const commit = (next: MomentDraft) => {
    setDraft(next);
    const instant = momentInstant(next, timeZone);
    if (!sameInstant(instant, value)) onChange(instant);
  };

  const incomplete = (draft.date === null) !== (draft.time === null);
  const impossible =
    draft.date !== null && draft.time !== null && timeZone !== null &&
    momentInstant(draft, timeZone) === null;
  const blocking = !timeZone
    ? t('moment.timeZoneUnknown')
    : impossible
      ? t('moment.doesNotExist')
      : null;
  const missingHalf = incomplete
    ? draft.date
      ? t('moment.timeStillNeeded')
      : t('moment.dateStillNeeded')
    : null;
  // A half left out is an instruction while the person is still typing, and
  // becomes the error when the page reports the field as wrong — so a
  // "required" never appears under a date that is plainly filled in.
  const errorText = blocking ?? (common.error ? (missingHalf ?? common.error) : null);
  const hintText = errorText ? null : (missingHalf ?? common.description ?? null);
  const invalid = errorText !== null;
  const required = common.isRequired ?? false;
  const disabled = common.isDisabled ?? false;
  const readOnly = common.isReadOnly ?? false;

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      aria-describedby={helperId}
      className={`flex w-full flex-col ${common.className ?? ''}`}
      data-slot="date-time-field"
    >
      <span
        id={labelId}
        className={`block pb-2 text-small text-foreground ${required ? "after:ms-0.5 after:text-danger after:content-['*']" : ''}`}
      >
        {common.label}
      </span>
      <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-2">
        <DatePicker
          aria-label={`${common.label}: ${t('moment.date')}`}
          aria-describedby={helperId}
          variant="bordered"
          labelPlacement="outside"
          value={parseOrNull(draft.date, parseDate)}
          // Typed through `CalendarDate` explicitly, as in `DateField`.
          onChange={(next: CalendarDate | null) =>
            commit({ ...draft, date: next ? next.toString() : null })
          }
          isRequired={required}
          isDisabled={disabled}
          isReadOnly={readOnly}
          isInvalid={invalid}
          {...(common.name ? { name: `${common.name}-date` } : {})}
          showMonthAndYearPickers
          // No HeroUI popover animation: its last frame flashed (see SelectField).
          disableAnimation
          classNames={DATE_PICKER_CLASSES}
        />
        <TimeInput
          aria-label={`${common.label}: ${t('moment.time')}`}
          aria-describedby={helperId}
          variant="bordered"
          labelPlacement="outside"
          // Minutes, not seconds: a person writes down the time to the minute,
          // and asking for more precision than was observed invents it.
          granularity="minute"
          hideTimeZone
          // HeroUI resolves its own patch release of @internationalized/date,
          // whose `Time` differs from ours only by a private brand; the
          // object is the same shape at runtime.
          value={parseOrNull(draft.time, parseTime) as unknown as TimeValue | null}
          onChange={(next: TimeValue | null) =>
            commit({
              ...draft,
              time: next ? `${twoDigits(next.hour)}:${twoDigits(next.minute)}` : null,
            })
          }
          isRequired={required}
          isDisabled={disabled}
          isReadOnly={readOnly}
          isInvalid={invalid}
          {...(common.name ? { name: `${common.name}-time` } : {})}
          classNames={DATE_READABILITY}
        />
      </div>
      <div id={helperId} className="flex flex-col gap-0.5 p-1" aria-live="polite">
        {errorText ? (
          <p className="text-tiny text-danger">{errorText}</p>
        ) : hintText ? (
          <p className="text-tiny text-foreground-400">{hintText}</p>
        ) : null}
        {timeZone ? (
          <p className="text-tiny text-foreground-400">
            {t('moment.laboratoryTime', { zone: timeZone })}
          </p>
        ) : null}
      </div>
    </div>
  );
}
